import { createEndpointer } from './endpointer';
import { createTurnClock } from './turnEnd';
import { languageOf } from './languageOf';
import { floatToPcm16, pcm16ToBase64, pcm16ToWavBlob, resampleTo24k, rmsOf } from './pcm';
import { createCapture as realCreateCapture } from './capture';

/**
 * THE LIVE EARS. A WebSocket to an OpenAI realtime TRANSCRIPTION session, opened with the short-lived client secret our
 * server minted (never a key), fed PCM16 24 kHz from the microphone. The words arrive as deltas while the person speaks;
 * the model takes no server VAD, so the client ends each turn (energy + ~700 ms of quiet) with a commit. The final words
 * carry a language: the one the ears named, else the script of the words (languageOf).
 *
 *   const ears = startLiveEars({ getSession, onPartial, onFinal, onState, onError, onLevel, continuous })
 *   await ears.ready            // resolves when listening (rejects after onError on the way in)
 *   ears.commit()               // end the turn now (a held note released)
 *   ears.pause() / ears.resume()// the microphone gated while a reply is spoken or the person has muted
 *   ears.close()                // the socket and the microphone, let go
 *
 * `recheck(blob) -> { text, language }` is the second opinion (the app's clip door): the turn's own audio, as a WAV, is posted when the
 * turn ends, and ITS words are the turn's the moment they arrive (the live model streams the first word with no language fixed:
 * "Karma" for כמה); the live words stay the caption, and stand when the clip cannot be heard.
 * `getSession` is the transport: () => Promise<{ token, wsUrl }>. `continuous: false` is hold-to-record: everything is
 * sent from the start and only commit() ends the turn. Errors are { code: 'mic-blocked' | 'session' | 'socket' |
 * 'provider' | 'closed', cause } so the caller can fall back to a recorded clip.
 */
export const SOCKET_OPEN_TIMEOUT_MS = 7_000; // a socket that is not open by now never will be: fall back to the clip ears
export const IDLE_CLOSE_MS = 60_000; // a hands-free session where nobody speaks is let go (the token bounds only the START)
const PREROLL_FRAMES = 12; // ~240 ms kept so the first syllable is not clipped
export const RECHECK_WAIT_MS = 2_500; // the live words wait this long for the clip's second opinion, then stand
const RECHECK_MIN_SAMPLES = 24_000 * 0.3; // a turn shorter than this is not worth a second opinion
const EARLY_MAX_MS = 8_000; // what was said before the socket opened: kept this much, newest first

export function startLiveEars({ getSession, onPartial, onFinal, onState, onError, onLevel, recheck, recheckWaitMs = RECHECK_WAIT_MS, continuous = true, endSilenceMs = 700, deps = {} } = {}) {
  const WS = deps.WebSocket || (typeof WebSocket !== 'undefined' ? WebSocket : null);
  const createCapture = deps.createCapture || realCreateCapture;
  const ep = createEndpointer({ endSilenceMs });
  const turn = createTurnClock(); // the turn ends on words (turnEnd.js), the energy only starts it
  let peak = 0;
  let ws = null;
  let capture = null;
  let closed = false;
  let paused = false;
  let open = false;
  let sending = !continuous; // continuous: only from speech start (with a preroll); held: from the start
  let hasAudio = false;
  let commitRequested = false; // a release that came before the socket was open
  let preroll = [];
  let early = []; // what was said before the socket opened (the token takes a moment), heard first
  let earlyMs = 0;
  let turnPcm = []; // the PCM16 of the turn that is being sent, kept for the second opinion
  let turnSamples = 0;
  const turns = []; // committed turns waiting for their words: { itemId, liveDone, delivered, bad, timer, live }
  let words = '';
  let item = null;
  let state = 'connecting';
  let idleTimer = null;
  const clearIdle = () => { if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; } };
  const armIdle = () => {
    clearIdle();
    if (!continuous || !open || paused || closed) return;
    idleTimer = setTimeout(() => { idleTimer = null; fail('idle'); api.close(); }, IDLE_CLOSE_MS);
  };
  const setState = (s) => { if (s !== state) { state = s; onState?.(s); } };
  const fail = (code, cause) => { if (closed) return; onError?.({ code, cause }); };

  const send = (ev) => { if (ws && ws.readyState === 1) ws.send(JSON.stringify(ev)); };
  const append = (frame, rate) => {
    const pcm = floatToPcm16(resampleTo24k(frame, rate));
    send({ type: 'input_audio_buffer.append', audio: pcm16ToBase64(pcm) });
    hasAudio = true;
    if (recheck) { turnPcm.push(pcm); turnSamples += pcm.length; }
  };

  // The turn is delivered ONCE: with the clip's words if they came, else the live model's.
  function deliver(rec, text, language) {
    if (rec.delivered || closed) return;
    rec.delivered = true;
    if (rec.timer) { clearTimeout(rec.timer); rec.timer = null; }
    if (rec.liveDone) turns.splice(turns.indexOf(rec), 1);
    setState(continuous ? 'listening' : 'finishing');
    armIdle();
    onFinal?.({ text, language: language || languageOf(text) || undefined });
  }
  function startRecheck(chunks) {
    const rec = { itemId: null, liveDone: false, delivered: false, bad: false, timer: null, live: null };
    turns.push(rec);
    let p;
    try { p = Promise.resolve(recheck(pcm16ToWavBlob(chunks, 24000))); } catch (e) { p = Promise.reject(e); }
    p.then((out) => {
      const text = String(out?.text ?? '').trim();
      if (!text) throw new Error('the clip said nothing');
      const named = String(out?.language ?? '').trim().toLowerCase().slice(0, 2);
      deliver(rec, text, named || undefined);
    }).catch(() => {
      rec.bad = true;
      if (rec.liveDone && !rec.delivered) deliver(rec, rec.live.text, rec.live.language); // the live words were waiting
    });
  }

  function commit() {
    if (!open) { commitRequested = true; return; }
    if (!hasAudio) {
      // A held note with nothing in it: end it with no words so the caller never waits for an answer that will not come.
      if (!continuous) { setState('finishing'); onFinal?.({ text: '' }); }
      return;
    }
    send({ type: 'input_audio_buffer.commit' });
    if (recheck && turnSamples >= RECHECK_MIN_SAMPLES) startRecheck(turnPcm);
    turnPcm = []; turnSamples = 0;
    hasAudio = false;
    sending = false;
    turn.reset();
    ep.reset();
    preroll = [];
    setState('finishing');
  }

  function onFrame(frame, rate) {
    if (closed) return;
    // Not open yet (the token and the socket take 1-7 s): whatever is said now is kept, bounded, and heard in order the
    // moment the socket opens - in a held note AND in hands-free voice mode (a person taps and speaks at once).
    if (!open) { if (!paused) { early.push({ frame, rate }); earlyMs += (frame.length / rate) * 1000; while (earlyMs > EARLY_MAX_MS && early.length > 1) { const d = early.shift(); earlyMs -= (d.frame.length / d.rate) * 1000; } } return; }
    const dtMs = (frame.length / rate) * 1000;
    if (paused) return;
    const level = rmsOf(frame);
    onLevel?.(level);
    if (!continuous) { if (sending) append(frame, rate); return; }
    const edge = ep.push(level, dtMs);
    peak = Math.max(peak, level);
    if (edge === 'start') {
      clearIdle();
      sending = true;
      words = '';
      peak = level;
      turn.start(); // sound has started a turn; "hearing you" waits for WORDS (the first delta)
      for (const p of preroll) append(p.frame, p.rate);
      preroll = [];
    }
    if (sending) append(frame, rate);
    else { preroll.push({ frame, rate }); if (preroll.length > PREROLL_FRAMES) preroll.shift(); }
    if (edge === 'end') { commit(); return; }
    // Words end a turn that quiet never would (music in the room), and sound with no words is noise.
    const verdict = sending ? turn.tick(dtMs, ep.quietMs) : null;
    if (verdict === 'commit') commit();
    else if (verdict === 'discard') discardNoise();
  }

  function discardNoise() {
    send({ type: 'input_audio_buffer.clear' });
    hasAudio = false; sending = false; words = ''; preroll = []; turnPcm = []; turnSamples = 0;
    turn.reset();
    ep.learnNoise(peak);
    setState('listening');
    onPartial?.('');
  }

  function onMessage(e) {
    let ev;
    try { ev = JSON.parse(e.data); } catch (_) { return; }
    if (ev.type === 'conversation.item.input_audio_transcription.delta') {
      if (ev.item_id !== item) { item = ev.item_id; words = ''; }
      words += ev.delta || '';
      turn.delta(words);
      if (continuous && sending && words.trim() && state !== 'hearing') setState('hearing');
      onPartial?.(words);
    } else if (ev.type === 'input_audio_buffer.committed') {
      const rec = turns.find((t) => !t.itemId); // the first turn that has not been given its item yet
      if (rec && ev.item_id) rec.itemId = ev.item_id;
    } else if (ev.type === 'conversation.item.input_audio_transcription.completed') {
      const text = String(ev.transcript ?? '').trim();
      const named = String(ev.languages?.[0]?.code ?? ev.language ?? '').trim().toLowerCase().slice(0, 2);
      words = ''; item = null;
      const live = { text, language: named || languageOf(text) || undefined };
      const rec = recheck ? (turns.find((t) => t.itemId && t.itemId === ev.item_id) || turns.find((t) => !t.liveDone)) : null;
      if (!rec) {
        setState(continuous ? 'listening' : 'finishing');
        armIdle();
        onFinal?.(live);
      } else {
        rec.liveDone = true; rec.live = live;
        if (rec.delivered) turns.splice(turns.indexOf(rec), 1); // the clip's words were already the turn's
        else if (rec.bad) deliver(rec, live.text, live.language);
        else rec.timer = setTimeout(() => { rec.timer = null; deliver(rec, live.text, live.language); }, recheckWaitMs); // the clip is slow: the live words stand
      }
    } else if (ev.type === 'error') {
      fail('provider', ev.error);
    }
  }

  const ready = (async () => {
    if (!WS) { fail('socket', new Error('no WebSocket')); throw new Error('no WebSocket'); }
    // The microphone and the token are asked for together: the microphone inside the tap's gesture, the token in parallel.
    const [cap, session] = await Promise.all([
      createCapture({ onFrame }).catch((e) => { const blocked = /NotAllowed|Permission|Security/i.test(e?.name || ''); fail(blocked ? 'mic-blocked' : 'socket', e); throw e; }),
      getSession().catch((e) => { fail('session', e); throw e; }),
    ].map((p) => p.catch((e) => ({ __err: e })))).then(([a, b]) => {
      if (a?.__err || b?.__err) {
        if (a && !a.__err) { try { a.stop(); } catch (_) { /* gone */ } }
        throw a?.__err || b?.__err;
      }
      return [a, b];
    });
    capture = cap;
    if (closed) { cap.stop(); return; }
    await new Promise((resolve, reject) => {
      try { ws = new WS(session.wsUrl, ['realtime', `openai-insecure-api-key.${session.token}`]); } catch (e) { fail('socket', e); reject(e); return; }
      const timer = setTimeout(() => {
        if (open || closed) return;
        const e = new Error('socket did not open');
        fail('socket', e); try { ws.close(); } catch (_) { /* gone */ } reject(e);
      }, SOCKET_OPEN_TIMEOUT_MS);
      ws.onopen = () => {
        clearTimeout(timer); open = true; armIdle(); setState('listening'); resolve();
        const kept = early; early = []; earlyMs = 0;
        // A held note sends it all; hands-free voice mode runs it through the same ears as live sound, so the turn starts, ends and commits as if it had just been said.
        for (const e of kept) { if (continuous) onFrame(e.frame, e.rate); else append(e.frame, e.rate); }
        if (commitRequested) { commitRequested = false; commit(); }
      };
      ws.onmessage = onMessage;
      ws.onerror = (e) => { if (!open) { clearTimeout(timer); fail('socket', e); reject(e); } };
      ws.onclose = (e) => { clearTimeout(timer); const was = open; open = false; if (!closed) { if (!was) { reject(new Error('closed')); } else fail('closed', e); } };
    });
  })();
  ready.catch(() => { try { capture?.stop(); } catch (_) { /* gone */ } });

  const api = {
    ready,
    commit,
    pause() { paused = true; clearIdle(); ep.reset(); preroll = []; if (continuous) sending = false; },
    resume() { paused = false; ep.reset(); armIdle(); },
    close() {
      if (closed) return;
      closed = true; open = false; clearIdle();
      for (const t of turns) if (t.timer) { clearTimeout(t.timer); t.timer = null; }
      try { capture?.stop(); } catch (_) { /* gone */ }
      try { ws?.close(); } catch (_) { /* gone */ }
      setState('closed');
    },
    get state() { return state; },
  };
  return api;
}
